import { ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionSummary, Box, FormControlLabel, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import InfoIcon from "../../../../../assets/images/icons/InfoIcon";
import BriaCheckbox from "../../../../../components/common/BriaCheckbox/BriaCheckbox";
import BriaInput from "../../../../../components/common/BriaInput/BriaInput";
import BriaSlider from "../../../../../components/common/BriaSlider/BriaSlider";
import BriaTooltip from "../../../../../components/common/BriaTooltip/BriaTooltip";
import { useAppStore } from "../../../../../hooks/useStores";
import InputLayout from "../../../../../layout/InputLayout/InputLayout";
import { tgAdvancedConfig } from "../../../../NewTG/App/Image/Config/TgImageGenerationConfig";
import styles from "../TextToImageConfig.module.scss";
import TextToImageSteps from "./Steps/TextToImageSteps";
import BriaSwitch from "../../../../../components/common/BriaSwitch/BriaSwitch.tsx";
import iframeStore from "../../../../IframeNew/iframe-store.tsx";
import LinkConstants from "../../../../../constants/LinkConstants.ts";

const TextToImageAdvanced = () => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.textToImage.config" });
	const { textToImageStore, imageToImageStore } = useAppStore();
	const { config, handleConfigChange } = textToImageStore;
	const hideNegativePrompt = config.fast || (!config.tailored_model_id && config.model_name?.toLowerCase().includes("fast"))
	const maxNumResults =
		(config.tailored_model_id
			? tgAdvancedConfig.max_results
			: textToImageStore.models[config.model]?.max_results) || 4;

	const handleNumResultsChange = (_event: any, numResults: number | number[]) => {
		handleConfigChange("num_results", numResults as number);
	};

	const handleContentModerationChange = () => {
		handleConfigChange("content_moderation", !(textToImageStore.config?.content_moderation));
		imageToImageStore.handleConfigChange("content_moderation", !(imageToImageStore.config?.content_moderation ?? false))
	}

	return (
		<Accordion elevation={0} disableGutters TransitionProps={{ timeout: 200 }} className={styles.advancedAccordion}>
			<AccordionSummary color="initial" expandIcon={<ExpandMore />} className={styles.accordionSummary}>
				{t("advanced")}
			</AccordionSummary>
			{ !iframeStore.isIframe() &&
				<Box className={styles.contentModerationContainer}>
					<Box className={styles.contentModerationTextContainer}>
						<Typography className={styles.contentModerationText}>{t("contentModeration.label")}</Typography>
						<BriaTooltip
							title={`${
								t("contentModeration.tooltip.info")
							  } <a href="${LinkConstants.CONTENT_MODERATION_BRIA_API_DOC}" target="_blank" rel="noopener noreferrer">Learn more</a>`}
							className={styles.info}
							displayOn="click">
								<InfoIcon className={styles.infoIcon} />
						</BriaTooltip>
					</Box>
					<BriaSwitch checked={(textToImageStore.config?.content_moderation ?? false)} onToggle={handleContentModerationChange} />
				</Box>
			}
			<Box className={styles.advanced}>
				<InputLayout
					label={t("negativePrompt.label")}
					labelClassName={styles.inputLabel}
					info={
						hideNegativePrompt || textToImageStore.hideNegativePrompt
							? t("negativePrompt.disabledInfo")
							: textToImageStore.imageReference.url.length > 0
							? t("negativePrompt.imageReferenceInfo")
							: t("negativePrompt.info")
					}
				>
					<BriaInput
						value={textToImageStore.config?.negative_prompt}
						onChange={(e) => textToImageStore.handleConfigChange("negative_prompt", e.target.value)}
						multiline
						minRows={4}
						placeholder={
							hideNegativePrompt
								? t("negativePrompt.disabledPlaceholder")
								: textToImageStore.imageReference.url.length > 0
								? t("negativePrompt.imageReferencePlaceholder")
								: t("negativePrompt.placeholder")
						}
						inputProps={{ className: styles.inputBase }}
						disabled={hideNegativePrompt || textToImageStore.hideNegativePrompt}
						italicPlaceholderClass={styles.italicPlaceholderClass}
					/>
				</InputLayout>
				<InputLayout
					label={t("numberOfImages.label")}
					labelClassName={styles.inputLabel}
					info={t("numberOfImages.info")}
				>
					<BriaSlider
						value={config.num_results}
						onChange={handleNumResultsChange}
						step={1}
						min={1}
						max={maxNumResults}
						marks={Array.from({ length: maxNumResults }, (_, index) => ({
							value: index + 1,
							label: `${index + 1}`,
						}))}
					/>
				</InputLayout>
				{!textToImageStore.isTextToVector() && (
					<Box className={styles.refineFacesCheckbox}>
						<FormControlLabel
							control={
								<BriaCheckbox
									checked={textToImageStore.config?.enhance_image}
									onChange={(e) =>
										textToImageStore.handleConfigChange("enhance_image", e.target.checked)
									}
								/>
							}
							label={t("enhanceImage.label")}
						/>
						<BriaTooltip title={t("enhanceImage.info")} className={styles.info} displayOn="click">
							<InfoIcon className={styles.infoIcon} />
						</BriaTooltip>
					</Box>
				)}

				<TextToImageSteps />

				<InputLayout
					label={t("seedNumber.label")}
					labelClassName={styles.inputLabel}
					info={t("seedNumber.info")}
				>
					<BriaInput
						type="number"
						value={config?.seed}
						onChange={(e) =>
							handleConfigChange("seed", e.target.value ? parseFloat(e.target.value) : undefined)
						}
						placeholder={t("seedNumber.placeholder")}
						height="37px"
						inputProps={{ className: styles.inputBase }}
					/>
					<FormControlLabel
						control={
							<BriaCheckbox
								checked={config?.displaySeed}
								onChange={(e) => handleConfigChange("displaySeed", e.target.checked)}
							/>
						}
						label={t("seedNumber.checkbox")}
					/>
				</InputLayout>
			</Box>
		</Accordion>
	);
};

export default observer(TextToImageAdvanced);
